import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import StepRow from '../components/StepRow';
import PartnerEntry from '../components/PartnerEntry';

export default function HowItWorks() {
  return (
    <>
      <PageHero
        eyebrow="How it works"
        title="A private marketplace, run on"
        emphasis="permission."
        lede="OfficerCapital pairs guard company owners with vetted lenders, investors, and acquirers. Every step is built so nothing about your business goes anywhere you didn't approve. Here's the whole flow, end to end."
      >
        <a href="https://officercapital.com/register" className="oc-btn oc-btn--primary">List your company</a>
        <Link to="/pricing" className="oc-btn oc-btn--link">See pricing →</Link>
      </PageHero>

      {/* THE FLOW IN DETAIL */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-mb-5">
            <div className="oc-eyebrow-gray oc-mb-2">The flow</div>
            <h2 className="oc-h2">
              Six checkpoints. You <em className="oc-em">control</em> every one.
            </h2>
          </div>

          <div className="oc-stack-lg">
            <FlowStep
              number="01"
              title="You build a listing"
              body="Choose what you're looking for — financing, an investor, a sale, or a combination. Fill in headline metrics: revenue, EBITDA, officer count, geography by region, customer mix by category and size. Upload financials if you want — they stay sealed until a partner you approve unlocks them. We never ask for your company name."
            />
            <FlowStep
              number="02"
              title="The listing goes live, anonymized"
              body="Vetted capital partners see the listing on the marketplace. They see your numbers. They don't see your name, your customer names, your exact geography, or your documents. OfficerApps customers get a priority badge so their listing surfaces higher in partner search results."
            />
            <FlowStep
              number="03"
              title="Partners request access"
              body="When a lender, investor, or acquirer wants to evaluate further, they pay an unlock fee — $500 for lenders and investors, $1,500 for acquirers. The fee buys them access to your full file. You're notified. You see exactly who they are: name, firm, prior deals."
            />
            <FlowStep
              number="04"
              title="You approve, or you don't"
              body="Each access request needs your explicit approval. Approval is per-partner — saying yes to one lender doesn't reveal you to anyone else. You can decline without explanation. You can take your time. Nothing breaks if you sit on it."
            />
            <FlowStep
              number="05"
              title="Messaging opens"
              body="Once you approve, that partner gets full access to your listing and a direct messaging channel. Take the conversation off-platform whenever you want — there's no requirement to keep it inside OfficerCapital. We're an introduction layer, not a middleman."
            />
            <FlowStep
              number="06"
              title="You close, or you walk"
              body="Sign whatever term sheet you negotiate, on whatever terms. We don't take a percentage of your financing, your investment, or your sale price. If a deal doesn't pan out, the listing stays up — or you pull it down with a single click."
            />
          </div>
        </div>
      </section>

      {/* THE DISCRETION MODEL */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">The discretion model</div>
              <h2 className="oc-h2">
                Three layers of <em className="oc-em">access control</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                A typical M&amp;A site treats access as binary — either it's a deal room or it's the public internet.
                OfficerCapital splits the difference into three layers, so partners see only what they need at each
                stage of evaluation.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <Layer
                  label="Layer 01 · Public listing"
                  what="Anonymized headline metrics."
                  who="Visible to any vetted, subscribed capital partner."
                />
                <hr className="oc-rule" />
                <Layer
                  label="Layer 02 · Unlocked file"
                  what="Full financials, customer mix detail, contract structure summaries."
                  who="Visible only to a partner who paid the unlock fee, AND was approved by you."
                />
                <hr className="oc-rule" />
                <Layer
                  label="Layer 03 · Direct contact"
                  what="Your identity, customer names, exact contracts."
                  who="Shared at your discretion, in messaging or off-platform."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VETTING */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-4">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Vetting</div>
              <h2 className="oc-h2">
                How partners <em className="oc-em">earn the seat</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              No partner sees a single listing without going through manual review first.
              Then they pay to keep their access.
            </p>
          </div>

          <div className="oc-grid-3">
            <VetCard
              label="Application"
              body="Lenders, investors, and acquirers apply with firm details, prior deal history, and references. We review every application by hand."
            />
            <VetCard
              label="Subscription"
              body="Approved partners pay a monthly subscription to access the marketplace. Lapsed subscriptions lose access to all listings, including ones they previously unlocked."
            />
            <VetCard
              label="Per-deal fees"
              body="Partners pay an unlock fee per listing — $500 for lenders and investors, $1,500 for acquirers — so they only engage with deals they're genuinely pursuing."
            />
          </div>
        </div>
      </section>

      {/* AUDIT */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Audit &amp; recordkeeping</div>
              <h2 className="oc-h2">
                Every access event, <em className="oc-em">logged</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                You see who unlocked your listing, when, and from where. We log it. They know we log it.
                If a partner shares your file with someone outside their firm, you have a record.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ListPoint title="Per-listing access log" body="Timestamps, IPs, and partner identity for every unlock and every document view." />
                <hr className="oc-rule" />
                <ListPoint title="One-click pause" body="Pause a listing instantly — partners with prior access lose visibility until you unpause." />
                <hr className="oc-rule" />
                <ListPoint title="Permanent removal" body="Remove the listing entirely and revoke all granted access in a single action." />
                <hr className="oc-rule" />
                <ListPoint title="No background sharing" body="OfficerCapital does not share, sell, or syndicate your listing data anywhere off the platform." />
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function FlowStep({ number, title, body }) {
  return (
    <div className="oc-grid-1-2" style={{ alignItems: 'start', gap: 64 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span className="oc-step__num">{number}</span>
          <span className="oc-eyebrow-gray">Step {number}</span>
        </div>
        <h3 className="oc-h3 oc-mt-2" style={{ fontSize: 26 }}>{title}</h3>
      </div>
      <p className="oc-body" style={{ paddingTop: 14, fontSize: 16, lineHeight: 1.7 }}>{body}</p>
    </div>
  );
}
function Layer({ label, what, who }) {
  return (
    <div>
      <div className="oc-eyebrow oc-mb-2">{label}</div>
      <div style={{ fontSize: 14, color: 'var(--oc-ink)', marginBottom: 4 }}>{what}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{who}</div>
    </div>
  );
}
function VetCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}
function ListPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
